import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  LandPlot,
  Loader2,
  MapPin,
  PanelLeftOpen,
  Play,
} from "lucide-react";
import { ApiError, landResearchApi } from "@/lib/api";
import { useSession } from "@/lib/useAuth";
import { useSidebar } from "@/lib/sidebar";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Alert } from "@/components/ui/Alert";
import { MapPickerModal } from "@/components/MapPickerModal";
import { QccMapModal } from "@/components/QccMapModal";
import { cn } from "@/lib/cn";

function isCoord(v: string, max: number): boolean {
  const n = Number(v);
  return v.trim() !== "" && Number.isFinite(n) && Math.abs(n) <= max;
}

export function LandResearchPage(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const sidebar = useSidebar();

  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [projectName, setProjectName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [qccMapOpen, setQccMapOpen] = useState(false);
  const [qccCompanyTotal, setQccCompanyTotal] = useState<number | null>(null);
  const [qccSelection, setQccSelection] = useState<import("@/lib/api").QccMapSelectionResponse | null>(null);
  const [pickedAddress, setPickedAddress] = useState<string | null>(null);
  // null = loading, "" = backend has no TENCENT_MAP_KEY configured.
  const [mapKey, setMapKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    landResearchApi
      .mapKey(session.token)
      .then((r) => {
        if (!cancelled) setMapKey(typeof r.key === "string" ? r.key : "");
      })
      .catch(() => {
        if (!cancelled) setMapKey("");
      });
    return () => {
      cancelled = true;
    };
  }, [session.token]);


  const coordsValid = isCoord(lat, 90) && isCoord(lng, 180);
  // Button stays clickable (only disabled while submitting) so an invalid coord
  // surfaces a clear error on submit instead of silently disabling the button.
  const canSubmit = !submitting;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!coordsValid) {
      setError(
        "请填写有效的项目 GPS：纬度 ∈ [-90, 90]，经度 ∈ [-180, 180]（注意别把经度填进纬度框）",
      );
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await landResearchApi.submit(session.token, {
        lat: lat.trim(),
        lng: lng.trim(),
        projectName: projectName.trim(),
        qccCompanyTotal,
        qccSelectionJson: qccSelection ? JSON.stringify(qccSelection) : undefined,
      });
      navigate(`/land/${res.conv_id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "提交失败，请重试");
      setSubmitting(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-canvas">
      <div className="mx-auto max-w-3xl space-y-5 px-6 py-6">
        <header>
          <div className="flex items-center gap-2 text-[14px] text-zinc-500">
            {sidebar.collapsed ? (
              <button onClick={sidebar.toggle} className="rounded-md p-1.5 hover:bg-zinc-100">
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            ) : null}
            <span>联东</span>
            <span className="text-zinc-300">/</span>
            <span className="text-zinc-800">地块数据</span>
          </div>
          <div className="mt-3 flex items-start gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand-900 text-white shadow-sm">
              <LandPlot className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-[20px] font-semibold tracking-tight text-brand-900">地块数据</h1>
            </div>
          </div>
        </header>

        <form onSubmit={onSubmit} className="animate-fade-in-up space-y-5">
          <Card className="overflow-hidden">
            <CardHeader
              title={<SectionTitle step="1" title="项目位置" required />}
            />
            <CardBody className="space-y-4">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setPickerOpen(true)}
                className={cn(
                  "group relative w-full overflow-hidden rounded-xl border text-left transition-all",
                  coordsValid
                    ? "border-[#CBE5CC] bg-gradient-to-br from-[#F1F9F1] to-[#EAF6EE] hover:border-[#A9D5AB]"
                    : "border-[#DCE5F5] bg-gradient-to-br from-[#F7FAFE] to-[#EEF3FB] hover:border-[#B9CBF0] hover:shadow-sm",
                  submitting && "cursor-not-allowed opacity-60",
                )}
              >
                <MapGridDecoration />
                <div className="relative flex items-center gap-4 px-5 py-6">
                  <span className="relative flex-shrink-0">
                    {/* breathing halo guides the eye to the primary action until a point is set */}
                    {!coordsValid ? (
                      <span className="absolute inset-0 animate-ping rounded-full bg-brand-300/50" />
                    ) : null}
                    <span
                      className={cn(
                        "relative flex h-12 w-12 items-center justify-center rounded-full shadow-sm ring-4 transition-transform duration-200 group-hover:scale-110",
                        coordsValid
                          ? "bg-good text-white ring-[#E1F3E2]"
                          : "bg-white text-brand-500 ring-brand-100",
                      )}
                    >
                      {coordsValid ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <MapPin className="h-5 w-5" />
                      )}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    {coordsValid ? (
                      <>
                        <span className="block truncate text-[16px] font-medium text-zinc-900">
                          {pickedAddress || "已填入项目坐标"}
                        </span>
                        <span className="mt-0.5 block font-mono text-[14px] text-[#4E9B51]">
                          {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="block text-[16px] font-medium text-zinc-900">
                          在腾讯地图上选点
                        </span>
                        <span className="mt-0.5 block text-[14px] text-zinc-500">
                          搜索地点或在地图上点选，自动带出经纬度（也可在下方手填）。
                        </span>
                      </>
                    )}
                  </span>
                  <span className="flex-shrink-0 rounded-md border border-brand-200 bg-white/90 px-2.5 py-1.5 text-[14px] font-medium text-brand-500 shadow-sm transition-colors group-hover:bg-white">
                    {coordsValid ? "重新选点" : "打开地图"}
                  </span>
                </div>
              </button>

              <div className="grid grid-cols-2 gap-4">
                <Field
                  id="lat"
                  label={<span className="text-[15px]">纬度 (latitude)</span>}
                  required
                >
                  <Input
                    id="lat"
                    value={lat}
                    onChange={(e) => {
                      setLat(e.target.value);
                      setPickedAddress(null);
                    }}
                    placeholder="例如 29.5503"
                    disabled={submitting}
                    inputMode="decimal"
                    className="font-mono text-[15px]"
                  />
                </Field>
                <Field
                  id="lng"
                  label={<span className="text-[15px]">经度 (longitude)</span>}
                  required
                >
                  <Input
                    id="lng"
                    value={lng}
                    onChange={(e) => {
                      setLng(e.target.value);
                      setPickedAddress(null);
                    }}
                    placeholder="例如 106.3202"
                    disabled={submitting}
                    inputMode="decimal"
                    className="font-mono text-[15px]"
                  />
                </Field>
              </div>

              <button
                type="button"
                disabled={submitting}
                onClick={() => setQccMapOpen(true)}
                className="flex w-full items-center gap-3 rounded-lg border border-brand-200 bg-[#F7FAFE] px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-100/60 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-brand-500 shadow-sm">
                  <Building2 className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-medium text-zinc-900">
                    企查查附近企业框选
                  </span>
                  <span className="mt-0.5 block text-[13px] text-zinc-500">
                    {qccCompanyTotal == null
                      ? "在当前页面打开企查查地图"
                      : `已框选 ${qccCompanyTotal.toLocaleString("zh-CN")} 家企业`}
                  </span>
                </span>
                <span className="shrink-0 text-[13px] font-medium text-brand-500">打开地图</span>
              </button>

              <Field
                id="project_name"
                label={<span className="text-[15px]">项目名称</span>}
                hint="可选"
              >
                <Input
                  id="project_name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="例如 高新西永项目"
                  disabled={submitting}
                  className="text-[15px]"
                />
              </Field>


            </CardBody>
          </Card>

          {error ? <Alert tone="error">{error}</Alert> : null}

          <div className="flex items-center justify-between rounded-lg bg-white px-5 py-3.5 shadow-card">
            <div className="flex items-center gap-4 text-[14px] text-zinc-500">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    coordsValid ? "bg-good" : "bg-zinc-300",
                  )}
                />
                坐标{coordsValid ? "已就绪" : "待填写"}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className={cn("h-1.5 w-1.5 rounded-full", coordsValid ? "bg-good" : "bg-zinc-300")} />
                企业5km自动取数
              </span>
            </div>
            <Button type="submit" disabled={!canSubmit} className="px-5 text-[15px]">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  提交中…
                </>
              ) : (
                <>
                  <Play className="h-3.5 w-3.5" />
                  开始数据搜索
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Outside the animated <form>: its transform would otherwise become
            the containing block for this fixed-position modal and break the
            viewport centering. */}
        <MapPickerModal
          open={pickerOpen}
          mapKey={mapKey}
          initialLat={lat}
          initialLng={lng}
          onPick={(loc) => {
            setLat(loc.lat.toFixed(6));
            setLng(loc.lng.toFixed(6));
            setPickedAddress([loc.poiname, loc.poiaddress].filter(Boolean).join(" · ") || null);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
        <QccMapModal
          open={qccMapOpen}
          token={session.token}
          onTotalChange={setQccCompanyTotal}
          onSelectionChange={setQccSelection}
          onClose={() => setQccMapOpen(false)}
        />
      </div>
    </div>
  );
}

function SectionTitle({
  step,
  title,
  required,
}: {
  step: string;
  title: string;
  required?: boolean;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 text-[17px]">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-900 text-[12px] font-semibold text-white">
        {step}
      </span>
      {title}
      <span className="rounded bg-brand-100 px-1.5 py-0.5 text-[11px] font-medium text-brand-500">
        {required ? "必填" : "可选"}
      </span>
    </span>
  );
}

// Subtle road-grid backdrop for the map-pick banner, echoing a map canvas:
// a fine grid plus two soft "road" curves, same visual language as MapPanel.
function MapGridDecoration(): JSX.Element {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full text-zinc-400/40"
      preserveAspectRatio="none"
      viewBox="0 0 720 110"
    >
      <defs>
        <pattern id="land-grid" width="36" height="36" patternUnits="userSpaceOnUse">
          <path d="M 36 0 L 0 0 0 36" fill="none" stroke="currentColor" strokeWidth="0.75" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#land-grid)" opacity="0.5" />
      <path
        d="M -10 78 C 120 40 260 108 400 64 S 640 50 730 24"
        fill="none"
        stroke="#ffffff"
        strokeWidth="10"
        strokeLinecap="round"
        opacity="0.85"
      />
      <path
        d="M 80 -8 C 150 40 120 78 230 96 S 420 116 520 104"
        fill="none"
        stroke="#dbe3f2"
        strokeWidth="5"
        strokeLinecap="round"
        opacity="0.8"
      />
    </svg>
  );
}
